// src/pages/TypingPractice.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { typingAPI } from "../services/api";
import TextSelector from "../components/typing/TextSelector";
import TypingArea from "../components/typing/TypingArea";
import StatsBar from "../components/typing/StatsBar";
import WordReplay from "../components/typing/WordReplay";
import "../styles/TypingPractice.css";
import Navbar from "../components/Navbar";

// ─── Helpers ──────────────────────────────────────────────────────
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatMMSS = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const getPerformanceLabel = (wpm) => {
  if (wpm >= 80) return { label: "🏆 Expert",       color: "#28a745" };
  if (wpm >= 60) return { label: "🥇 Advanced",     color: "#667eea" };
  if (wpm >= 40) return { label: "🥈 Intermediate", color: "#ffc107" };
  if (wpm >= 20) return { label: "🥉 Beginner",     color: "#fd7e14" };
  return           { label: "🌱 Novice",             color: "#94a3b8" };
};

const getAccuracyFeedback = (accuracy) => {
  if (accuracy >= 98) return "Flawless! 🌟";
  if (accuracy >= 95) return "Excellent! ✨";
  if (accuracy >= 85) return "Great job! 👍";
  if (accuracy >= 70) return "Keep practicing! 💪";
  return "Focus on accuracy! 🎯";
};

// ─── Timer color helper ────────────────────────────────────────────
const getFocusedTimerColor = (timeLeft, timeLimit) => {
  const pct = timeLeft / timeLimit;
  if (pct <= 0.1 || timeLeft <= 10) return "#ef4444";
  if (pct <= 0.25 || timeLeft <= 30) return "#f59e0b";
  return "#10b981";
};

// ─── Main Component ───────────────────────────────────────────────
const TypingPractice = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // ── Data from API ──
  const [paragraphs,        setParagraphs]        = useState([]);
  const [topics,            setTopics]            = useState([]);
  const [hasSubscription,   setHasSubscription]   = useState(false);
  const [subscription,      setSubscription]      = useState(null);
  const [dataLoading,       setDataLoading]       = useState(false);
  const [dataError,         setDataError]         = useState(null);

  // ── Session state ──
  const [textMode,          setTextMode]          = useState("predefined");
  const [typingMode,        setTypingMode]        = useState(null);
  const [selectedParagraph, setSelectedParagraph] = useState(null);
  const [customText,        setCustomText]        = useState("");
  const [customTime,        setCustomTime]        = useState(60);
  const [activeText,        setActiveText]        = useState("");
  const [activeTime,        setActiveTime]        = useState(60);
  const [sessionStarted,    setSessionStarted]    = useState(false);
  const [sessionFinished,   setSessionFinished]   = useState(false);
  const [finalStats,        setFinalStats]        = useState(null);
  const [language,          setLanguage]          = useState("english");
  const [difficulty,        setDifficulty]        = useState("all");
  const [topic,             setTopic]             = useState("all");

  const [liveStats, setLiveStats] = useState({
    wpm: 0, cpm: 0, accuracy: 100,
    errors: 0, keystrokes: 0, timeLeft: 0, progress: 0,
  });

  // ─── Fetch data ─────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!user) return;
    setDataLoading(true);
    setDataError(null);
    try {
      const params = { language };
      if (difficulty !== "all") params.difficulty = difficulty;
      if (topic !== "all")      params.topic      = topic;

      const [parasRes, subRes, topicsRes] = await Promise.all([
        typingAPI.getParagraphs(params),
        typingAPI.getMySubscription(),
        typingAPI.getTopics(language),
      ]);

      if (parasRes.data.success) {
        setParagraphs(parasRes.data.paragraphs);
        setHasSubscription(parasRes.data.hasSubscription);
      }
      if (subRes.data.success)    setSubscription(subRes.data.subscription);
      if (topicsRes.data.success) setTopics(topicsRes.data.topics);
    } catch (err) {
      console.error("fetchData error:", err);
      setDataError("Failed to load paragraphs. Please try again.");
    } finally {
      setDataLoading(false);
    }
  }, [user, language, difficulty, topic]);

  useEffect(() => {
    if (user) fetchData();
  }, [fetchData, user]);

  // ─── Handlers ───────────────────────────────────────────────────
  const handleSelectParagraph = (paragraph) => {
    setSelectedParagraph(paragraph);
    setActiveText(paragraph.text);
    setActiveTime(paragraph.estimatedTime);
    setTypingMode(null);
    setSessionStarted(false);
    setSessionFinished(false);
    setFinalStats(null);
    setLiveStats({
      wpm: 0, cpm: 0, accuracy: 100,
      errors: 0, keystrokes: 0,
      timeLeft: paragraph.estimatedTime, progress: 0,
    });
  };

  const handleStartSession = () => {
    if (!user) {
      navigate("/login", { state: { from: "/typing-practice" } });
      return;
    }
    if (textMode === "custom") {
      if (!customText.trim()) return;
      setActiveText(customText.trim());
      setActiveTime(customTime);
    }
    setSessionStarted(true);
    setSessionFinished(false);
    setFinalStats(null);
  };

  const handleSessionFinish = (stats) => {
    setFinalStats(stats);
    setSessionFinished(true);
    setSessionStarted(false);
  };

  const handleRestart = () => {
    setSessionStarted(false);
    setSessionFinished(false);
    setFinalStats(null);
    setTypingMode(null);
    setLiveStats({
      wpm: 0, cpm: 0, accuracy: 100,
      errors: 0, keystrokes: 0,
      timeLeft: activeTime, progress: 0,
    });
    setTimeout(() => setSessionStarted(true), 50);
  };

  const handleReset = () => {
    setSelectedParagraph(null);
    setCustomText("");
    setActiveText("");
    setActiveTime(60);
    setTypingMode(null);
    setSessionStarted(false);
    setSessionFinished(false);
    setFinalStats(null);
    setLiveStats({
      wpm: 0, cpm: 0, accuracy: 100,
      errors: 0, keystrokes: 0,
      timeLeft: 60, progress: 0,
    });
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setDifficulty("all");
    setTopic("all");
    handleReset();
  };

  const handleTypingModeChange = (newMode) => {
    setTypingMode(newMode);
    setSessionStarted(false);
    setSessionFinished(false);
    setFinalStats(null);
    setLiveStats({
      wpm: 0, cpm: 0, accuracy: 100,
      errors: 0, keystrokes: 0,
      timeLeft: activeTime || 60, progress: 0,
    });
  };

  // ── Exit focused mode ──
  const handleExitFocus = () => {
    setSessionStarted(false);
    setSessionFinished(false);
    setFinalStats(null);
    setTypingMode(null);
    setLiveStats({
      wpm: 0, cpm: 0, accuracy: 100,
      errors: 0, keystrokes: 0,
      timeLeft: activeTime || 60, progress: 0,
    });
  };

  // ── ESC key exits focused mode ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && sessionStarted) {
        handleExitFocus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [sessionStarted]);

  const canStart =
    typingMode !== null &&
    (textMode === "predefined"
      ? selectedParagraph !== null
      : customText.trim().length > 20);

  // ─── Focused Overlay (both practice + test) ──────────────────────
  const renderFocusedMode = () => {
    if (!sessionStarted) return null;

    const isTest      = typingMode === "test";
    const timeLeft    = liveStats.timeLeft;
    const timerColor  = getFocusedTimerColor(timeLeft, activeTime);
    const isUrgent    = timeLeft <= 10;
    const pct         = Math.max(0, (timeLeft / activeTime) * 100);

    return (
      <div className={`focused-overlay ${isTest ? "focused-overlay--test" : "focused-overlay--practice"}`}>

        {/* ── Top Bar: mode label + exit button ── */}
        <div className="focused-top-bar">
          <div className="focused-mode-pill">
            {isTest ? "🎯 Test Mode" : "📝 Practice Mode"}
          </div>
          <button
            className="focused-exit-btn"
            onClick={handleExitFocus}
            title="Exit (ESC)"
            aria-label="Exit focused mode"
          >
            ✕ Exit
          </button>
        </div>

        {/* ── Timer Block ── */}
        <div className={`focused-timer-block ${isUrgent ? "urgent" : ""}`}>
          <div className="focused-timer-label">⏱ Time Remaining</div>
          <div
            className="focused-timer-value"
            style={{ color: timerColor }}
          >
            {formatMMSS(timeLeft)}
          </div>
          <div className="focused-timer-track">
            <div
              className="focused-timer-fill"
              style={{
                width: `${pct}%`,
                background: timerColor,
                transition: "width 1s linear, background 0.5s ease",
              }}
            />
          </div>
          {isUrgent && timeLeft > 0 && (
            <div className="focused-timer-urgent">⚠️ Hurry up!</div>
          )}
        </div>

        {/* ── Live mini stats strip — practice mode only ── */}
        {!isTest && (
          <div className="focused-live-stats">
            <div className="focused-live-stat">
              <span className="focused-live-stat-value"
                style={{ color: "#667eea" }}>
                {liveStats.wpm}
              </span>
              <span className="focused-live-stat-label">WPM</span>
            </div>
            <div className="focused-live-divider" />
            <div className="focused-live-stat">
              <span
                className="focused-live-stat-value"
                style={{
                  color:
                    liveStats.accuracy >= 95 ? "#10b981" :
                    liveStats.accuracy >= 80 ? "#f59e0b" : "#ef4444",
                }}
              >
                {liveStats.accuracy}%
              </span>
              <span className="focused-live-stat-label">Accuracy</span>
            </div>
            <div className="focused-live-divider" />
            <div className="focused-live-stat">
              <span className="focused-live-stat-value"
                style={{ color: liveStats.errors === 0 ? "#10b981" : "#ef4444" }}>
                {liveStats.errors}
              </span>
              <span className="focused-live-stat-label">Errors</span>
            </div>
            <div className="focused-live-divider" />
            <div className="focused-live-stat">
              <span className="focused-live-stat-value"
                style={{ color: "#06b6d4" }}>
                {liveStats.progress}%
              </span>
              <span className="focused-live-stat-label">Progress</span>
            </div>
          </div>
        )}

        {/* ── Typing Area ── */}
        <div className="focused-typing-wrap">
          <TypingArea
            key={`${activeText}-${typingMode}`}
            text={activeText}
            timeLimit={activeTime}
            language={language}
            mode={typingMode}
            onStatsUpdate={setLiveStats}
            onFinish={handleSessionFinish}
            onRestart={handleRestart}
          />
        </div>

      </div>
    );
  };

  // ─── Inline Results ──────────────────────────────────────────────
  const renderInlineResults = () => {
    if (!finalStats) return null;
    const perf = getPerformanceLabel(finalStats.wpm);

    return (
      <div className="inline-results">

        {/* ── Header ── */}
        <div className="inline-result-header">
          <div className="inline-result-icon">
            {finalStats.completedFully ? "🏅" : "⏱️"}
          </div>
          <div className="inline-result-title-block">
            <h2 className="inline-result-title">
              {finalStats.completedFully ? "Text Completed!" : "Time's Up!"}
            </h2>
            <p className="inline-result-subtitle">
              "{textMode === "predefined"
                ? selectedParagraph?.title
                : "Custom Text"}"
              <span className={`result-mode-tag ${typingMode}`}>
                {typingMode === "test" ? "🧪 Test Mode" : "🏋️ Practice Mode"}
              </span>
            </p>
          </div>
          <div
            className="inline-perf-badge"
            style={{
              backgroundColor: `${perf.color}18`,
              borderColor: perf.color,
            }}
          >
            <span className="inline-perf-label" style={{ color: perf.color }}>
              {perf.label}
            </span>
            <span className="inline-perf-feedback">
              {getAccuracyFeedback(finalStats.accuracy)}
            </span>
          </div>
        </div>

        {/* ── Word-by-Word Visual Replay ── */}
        {finalStats.wordResults && finalStats.wordResults.length > 0 && (
          <WordReplay
            wordResults={finalStats.wordResults}
            mode={typingMode}
          />
        )}

        {/* ── Motivation ── */}
        <div className="inline-motivation">
          {finalStats.wpm >= 60 && finalStats.accuracy >= 90 ? (
            <p>🔥 Outstanding performance! You're a typing pro!</p>
          ) : finalStats.wpm >= 40 && finalStats.accuracy >= 80 ? (
            <p>💪 Great work! Keep pushing to reach the next level!</p>
          ) : finalStats.accuracy < 70 ? (
            <p>🎯 Slow down a little and focus on accuracy first!</p>
          ) : (
            <p>🌱 Practice makes perfect — you're improving every session!</p>
          )}
        </div>

      </div>
    );
  };

  // ─── Banners ─────────────────────────────────────────────────────
  const renderGuestBanner = () => (
    <div className="tp-guest-banner">
      <div className="tp-guest-icon">🔒</div>
      <div className="tp-guest-text">
        <h3>Login to Start Typing Practice</h3>
        <p>
          Create a free account to access typing practice and improve your
          speed and accuracy.
        </p>
      </div>
      <button
        className="tp-guest-login-btn"
        onClick={() =>
          navigate("/login", { state: { from: "/typing-practice" } })
        }
      >
        🚀 Login to Get Started
      </button>
    </div>
  );

  const renderSubscriptionStatus = () => {
    if (!user || !hasSubscription || !subscription) return null;
    return (
      <div className="tp-sub-active-badge">
        💎 Premium Active — {subscription.daysRemaining} days remaining
      </div>
    );
  };

  // ── WhatsApp Premium Banner ──
  const WHATSAPP_NUMBER = "917745002013";
  const PREMIUM_PRICE   = 69;

  const buildWhatsAppLink = () => {
    const message = encodeURIComponent(
      `Hi! I'm interested in buying the Typing Practice Premium Plan for ₹${PREMIUM_PRICE}. Please help me with the payment and activation. 🙏`
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  const PremiumBanner = ({ hasSubscription, subscription }) => {
    if (hasSubscription) return null;
    return (
      <div className="tp-premium-banner">
        <div className="tp-premium-banner-left">
          <div className="tp-premium-banner-icon">💎</div>
          <div className="tp-premium-banner-text">
            <h3 className="tp-premium-banner-title">
              Unlock All Premium Paragraphs
            </h3>
            <p className="tp-premium-banner-desc">
              Get access to all difficulty levels, topics, and languages.
              {subscription && !subscription.isValid && (
                <span className="tp-premium-expired-tag">
                  {" "}⏰ Your plan expired.
                </span>
              )}
            </p>
            <div className="tp-premium-price-row">
              <span className="tp-premium-price">₹{PREMIUM_PRICE}</span>
              <span className="tp-premium-period">/ year</span>
              <span className="tp-premium-tag">🔥 Best Value</span>
            </div>
          </div>
        </div>
        <a
          href={buildWhatsAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          className="tp-whatsapp-btn"
          aria-label="Buy premium via WhatsApp"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="tp-whatsapp-icon"
            aria-hidden="true"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span>Buy Premium — ₹{PREMIUM_PRICE}</span>
        </a>
      </div>
    );
  };

  // ─── Mode Selector Cards ─────────────────────────────────────────
  const renderModeSelectorCards = () => {
    const textReady =
      textMode === "predefined"
        ? selectedParagraph !== null
        : customText.trim().length > 20;

    if (!textReady || sessionStarted || sessionFinished) return null;

    return (
      <div className="mode-selector-section">
        <div className="mode-selector-heading">
          <span className="mode-selector-heading-icon">🎮</span>
          <div>
            <h3 className="mode-selector-heading-title">Choose Your Mode</h3>
            <p className="mode-selector-heading-sub">
              {textMode === "predefined"
                ? `"${selectedParagraph?.title}" selected — how do you want to practice?`
                : "Custom text ready — how do you want to practice?"}
            </p>
          </div>
        </div>

        <div className="session-mode-selector">

          {/* ── Practice Card ── */}
          <div
            className={`session-mode-card practice-card ${
              typingMode === "practice" ? "selected" : ""
            }`}
            onClick={() => handleTypingModeChange("practice")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && handleTypingModeChange("practice")
            }
            aria-pressed={typingMode === "practice"}
          >
            <span className="mode-card-check" aria-hidden="true">✓</span>
            <span className="mode-card-icon" aria-hidden="true">📝</span>
            <h3 className="mode-card-title">Practice Mode</h3>
            <p className="mode-card-desc">
              Type at your own pace with live colour feedback. Green for
              correct words, red for mistakes. Perfect for building speed
              and muscle memory.
            </p>
            <div className="mode-card-tags">
              <span className="mode-card-tag">✅ Live feedback</span>
              <span className="mode-card-tag">⏱️ Flexible timer</span>
              <span className="mode-card-tag">🔁 Retry anytime</span>
            </div>
          </div>

          {/* ── Test Card ── */}
          <div
            className={`session-mode-card test-card ${
              typingMode === "test" ? "selected" : ""
            }`}
            onClick={() => handleTypingModeChange("test")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && handleTypingModeChange("test")
            }
            aria-pressed={typingMode === "test"}
          >
            <span className="mode-card-check" aria-hidden="true">✓</span>
            <span className="mode-card-icon" aria-hidden="true">🎯</span>
            <h3 className="mode-card-title">Test Mode</h3>
            <p className="mode-card-desc">
              Simulate a real exam. No live hints — results are revealed
              only after submission. Strict timer. Mirrors Bombay High
              Court typing test pattern.
            </p>
            <div className="mode-card-tags">
              <span className="mode-card-tag">🚫 No live hints</span>
              <span className="mode-card-tag">⏳ Strict timer</span>
              <span className="mode-card-tag">📊 Full report</span>
            </div>
          </div>

        </div>

        {typingMode === "test" && (
          <div className="test-mode-info-banner">
            <div className="test-mode-info-icon">🧪</div>
            <div className="test-mode-info-text">
              <strong>Test Mode — Bombay High Court Pattern</strong>
              <ul className="test-mode-rules-list">
                <li>🚫 No backspace allowed</li>
                <li>🚫 No error highlights while typing</li>
                <li>⏱️ Timer always visible</li>
                <li>✅ Submit when done — errors revealed after submission</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── JSX ─────────────────────────────────────────────────────────
  return (
    <>
      {/* ══════════════════════════════════════════════════════════
          FOCUSED OVERLAY — active for BOTH practice and test
          while session is running
      ══════════════════════════════════════════════════════════ */}
      {renderFocusedMode()}

      {/* ══════════════════════════════════════════════════════════
          NORMAL PAGE — always rendered beneath the overlay
      ══════════════════════════════════════════════════════════ */}
      <div className="typing-practice-page">
        <Navbar />
        <br /><br /><br /><br />

        {/* ── Page Header ── */}
        <div className="typing-page-header">
          <div className="typing-header-content">
            <h1 className="typing-page-title">
              <span className="typing-icon">⌨️</span>
              Typing Practice
            </h1>
            <p className="typing-page-subtitle">
              Improve your speed and accuracy in English &amp; Marathi
            </p>
          </div>
          <div className="language-toggle">
            <button
              className={`lang-btn ${language === "english" ? "active" : ""}`}
              onClick={() => handleLanguageChange("english")}
              disabled={sessionStarted}
            >
              🇬🇧 English
            </button>
            <button
              className={`lang-btn ${language === "marathi" ? "active" : ""}`}
              onClick={() => handleLanguageChange("marathi")}
              disabled={sessionStarted}
            >
              🇮🇳 Marathi
            </button>
          </div>
        </div>

        {renderSubscriptionStatus()}

        {user && (
          <PremiumBanner
            hasSubscription={hasSubscription}
            subscription={subscription}
          />
        )}

        {!user && renderGuestBanner()}

        {/* ── Text Source Selector ── */}
        {!sessionStarted && !sessionFinished && (
          <div className="selectors-row">
            <div className="selector-group">
              <span className="selector-group-label">📂 Text Source</span>
              <div className="mode-selector">
                <button
                  className={`mode-btn ${
                    textMode === "predefined" ? "active" : ""
                  }`}
                  onClick={() => {
                    setTextMode("predefined");
                    handleReset();
                  }}
                >
                  📚 Predefined Texts
                </button>
                <button
                  className={`mode-btn ${
                    textMode === "custom" ? "active" : ""
                  }`}
                  onClick={() => {
                    setTextMode("custom");
                    handleReset();
                  }}
                >
                  ✏️ Custom Text
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Main Content ── */}
        <div className="typing-main-content">

          {/* ── Predefined: Filters + Text Selector ── */}
          {textMode === "predefined" && !sessionStarted && !sessionFinished && (
            <>
              <div className="tp-filters-row">
                <select
                  className="tp-filter-select"
                  value={difficulty}
                  onChange={(e) => {
                    setDifficulty(e.target.value);
                    handleReset();
                  }}
                  disabled={sessionStarted}
                >
                  <option value="all">🎯 All Difficulties</option>
                  <option value="easy">🟢 Easy</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="hard">🔴 Hard</option>
                </select>

                <select
                  className="tp-filter-select"
                  value={topic}
                  onChange={(e) => {
                    setTopic(e.target.value);
                    handleReset();
                  }}
                  disabled={sessionStarted}
                >
                  <option value="all">📁 All Topics</option>
                  {topics.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {dataLoading && (
                <div className="tp-data-loading">⏳ Loading paragraphs...</div>
              )}
              {dataError && (
                <div className="tp-data-error">
                  ⚠️ {dataError}
                  <button onClick={fetchData} className="tp-retry-btn">
                    Retry
                  </button>
                </div>
              )}
              {!dataLoading && !dataError && paragraphs.length === 0 && (
                <div className="tp-data-empty">
                  📭 No paragraphs found for the selected filters.
                </div>
              )}
              {!dataLoading && !dataError && paragraphs.length > 0 && (
                <TextSelector
                  paragraphs={paragraphs}
                  selectedParagraph={selectedParagraph}
                  onSelect={handleSelectParagraph}
                  language={language}
                  activeTime={activeTime}
                  setActiveTime={setActiveTime}
                  hasSubscription={hasSubscription}
                />
              )}
            </>
          )}

          {/* ── Custom Text Input ── */}
          {textMode === "custom" && !sessionStarted && !sessionFinished && (
            <div className="custom-text-section">
              <div className="custom-text-card">
                <h3 className="custom-text-title">✏️ Enter Your Custom Text</h3>
                <p className="custom-text-hint">
                  Paste or type any text you want to practice. Minimum 20
                  characters.
                </p>
                <textarea
                  className="custom-textarea"
                  placeholder={
                    language === "marathi"
                      ? "येथे तुमचा मजकूर टाइप करा किंवा पेस्ट करा..."
                      : "Type or paste your practice text here..."
                  }
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  rows={6}
                />
                <div className="custom-char-count">
                  {customText.length} characters
                  {customText.length > 0 && customText.length < 20 && (
                    <span className="char-warning">
                      {" "}(minimum 20 required)
                    </span>
                  )}
                </div>
                <div className="custom-timer-row">
                  <label className="timer-label">⏱️ Set Timer</label>
                  <div className="timer-presets">
                    {[30, 60, 120, 180, 300].map((t) => (
                      <button
                        key={t}
                        className={`timer-preset-btn ${
                          customTime === t ? "active" : ""
                        }`}
                        onClick={() => setCustomTime(t)}
                      >
                        {t >= 60 ? `${t / 60}m` : `${t}s`}
                      </button>
                    ))}
                  </div>
                  <div className="timer-custom-input">
                    <input
                      type="number"
                      min={10}
                      max={600}
                      value={customTime}
                      onChange={(e) =>
                        setCustomTime(
                          Math.max(10, Math.min(600, Number(e.target.value)))
                        )
                      }
                      className="timer-number-input"
                    />
                    <span className="timer-unit">seconds</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Mode Selector Cards ── */}
          {renderModeSelectorCards()}

          {/* ── StatsBar — shown ONLY on results screen after session ends ── */}
          {sessionFinished && (
            <StatsBar
              stats={liveStats}
              mode={typingMode}
              timeLimit={activeTime}
              isFinished={true}
              finalStats={finalStats}
            />
          )}

          {/* ── Inline Results ── */}
          {sessionFinished && renderInlineResults()}

          {/* ── Start Button ── */}
          {!sessionStarted && !sessionFinished && (
            <div className="session-controls">
              {!user ? (
                <button
                  className="start-btn"
                  onClick={() =>
                    navigate("/login", {
                      state: { from: "/typing-practice" },
                    })
                  }
                >
                  🔒 Login to Start
                </button>
              ) : (
                <button
                  className={`start-btn ${!canStart ? "disabled" : ""} ${
                    typingMode === "test" ? "test-start-btn" : ""
                  }`}
                  onClick={handleStartSession}
                  disabled={!canStart}
                >
                  {!selectedParagraph && textMode === "predefined"
                    ? "⬆️ Select a text first"
                    : typingMode === null
                    ? "⬆️ Choose a mode above"
                    : typingMode === "test"
                    ? "🎯 Start Test"
                    : "🚀 Start Typing"}
                </button>
              )}
            </div>
          )}

          {/* ── Post-finish Buttons ── */}
          {sessionFinished && (
            <div className="session-controls">
              <button className="restart-btn" onClick={handleRestart}>
                🔄 Try Again
              </button>
              <button className="reset-btn" onClick={handleReset}>
                📚 Choose Another Text
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default TypingPractice;